import { useMutation } from '@apollo/client';
import React, { useCallback, useMemo } from 'react';
import { useLatest } from 'react-use';

import { Box } from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentMode,
  useAppointmentPatient,
  useIsAppointmentReadonly,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { DynamicSummary } from '@eluve/llm-outputs';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  DynamicArtifactRenderer,
  DynamicArtifactSaveContextProps,
} from '@eluve/smart-blocks';
import { convertArrayToPgLiteral } from '@eluve/utils';

const updateHumanOutputContentMutation = graphql(`
  mutation updateHumanOutputContent($id: uuid!, $path: _text!, $value: jsonb!) {
    updateHumanOutputContent(args: { _id: $id, path: $path, value: $value }) {
      __typename
      id
      content
      updatedAt
    }
  }
`);

export interface DynamicSummaryRendererProps {
  llmOutputId: string | null;
  humanOutputId: string | null;
  summary: DynamicSummary;
  onPendingChanges?: () => void;
  onSave?: () => void;
  onError?: (error: Error) => void;
}

export const DynamicSummaryRenderer: React.FC<DynamicSummaryRendererProps> = ({
  llmOutputId,
  humanOutputId,
  summary,
  onPendingChanges,
  onSave,
  onError,
}) => {
  const isReadonly = useIsAppointmentReadonly();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const appointmentId = useAppointmentId();
  const appointmentMode = useAppointmentMode();
  const patient = useAppointmentPatient();
  const analytics = useAnalytics('summary');

  const latestOnSave = useLatest(onSave);
  const latestOnError = useLatest(onError);

  const [updateHumanOutputContent] = useMutation(
    updateHumanOutputContentMutation,
  );

  const saveBlock: DynamicArtifactSaveContextProps['saveBlock'] | undefined =
    useMemo(() => {
      if (isReadonly || !humanOutputId) {
        return undefined;
      }

      return async (blockPath, value) => {
        if (!blockPath || value === undefined) {
          return;
        }

        await updateHumanOutputContent({
          variables: {
            id: humanOutputId,
            path: convertArrayToPgLiteral(blockPath),
            value: value as Record<string, unknown>,
          },
          onCompleted: () => {
            analytics.capture('summary_result_update', {
              tenantId,
              userId,
              appointmentId,
              appointmentMode,
              humanOutputId,
              blockPath: blockPath?.join(','),
            });
            latestOnSave.current?.();
          },
          onError: latestOnError.current,
        });
      };
      // latestOnSave latestOnError already use latest ref, dont include them in dep array
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isReadonly,
      humanOutputId,
      tenantId,
      userId,
      appointmentId,
      appointmentMode,
      updateHumanOutputContent,
      analytics,
    ]);

  const handlePendingChanges: DynamicArtifactSaveContextProps['onPendingChanges'] =
    useCallback(() => onPendingChanges?.(), [onPendingChanges]);

  return (
    <Box data-accessibility-label="note-summary">
      <DynamicArtifactRenderer
        key={llmOutputId}
        blocks={summary.blocks}
        isReadonly={isReadonly}
        saveBlock={saveBlock}
        onPendingChanges={handlePendingChanges}
        email={patient?.external_patients_info?.[0]?.email}
      />
    </Box>
  );
};
